import React, { useState } from 'react';
import PropTypes from 'prop-types';
import isBoolean from 'common/util/isBoolean';
import classnames from 'classnames';
import lodashCompact from 'lodash/fp/compact';
import pluralize from 'pluralize';

import {
  makeListingAddressStreetDisplay,
  makeListingFullAddressString,
} from 'common/listingHelpers';

import { SIGN_OTTER_ORDER_STATUS_SUBMITTED } from 'common/jobConstants';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import { SUNLOCK_STATUS_ACTIVE } from 'common/sunlocks/sunlockConstants';

import SrCard from 'client/components/SrCard/SrCard';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrGridRow from 'client/components/SrGridRow/SrGridRow';
import SrIcon from 'client/components/SrIcon';
import SrLink from 'client/components/SrLink/SrLink';
import SrSeparator from 'client/components/SrSeparator';
import SrSunlockCommandsLogModal from 'client/components/SrSunlockCommandsLogModal/SrSunlockCommandsLogModal';
import SrToursHealthFailedTours, {
  SrToursHealthFailedToursTable,
} from 'client/components/SrToursHealth/SrToursHealthFailedTours';
import SrTextGreycliff from 'client/components/SrTextGreycliff';
import SrListingNotesChip from 'client/components/SrListingNotesChip';

import './SrToursHealth.less';

const getTotalDuration = (interval) => {
  if (!interval) {
    return null;
  }

  const { days, hours, minutes, seconds } = interval;
  return lodashCompact([
    days ? `${days} ${pluralize('day', days, false)}` : '',
    hours ? `${hours} ${pluralize('hour', hours, false)}` : '',
    minutes ? `${minutes} ${pluralize('minute', minutes, false)}` : '',
    seconds ? `${seconds} ${pluralize('second', seconds, false)}` : '',
  ]).join(' ');
};

const getTotalShortDuration = (interval) => {
  if (!interval) {
    return null;
  }
  const { days, hours, minutes } = interval;
  return lodashCompact([
    days ? `${days}d` : '',
    hours ? `${hours}h` : '',
    minutes ? `${minutes}m` : '',
  ]).join(' ');
};

const getCantTourLabel = ({ totalTurnOns, totalTurnOnTime, lastTurnOn }) => {
  const lastTourOnText = lastTurnOn
    ? `Currently marked as can't be toured by ${lastTurnOn.userName}
      on ${momentWithAustinTimezone(lastTurnOn.lastTurnOnDate).format('MMM D [at] ha')}`
    : "Currently marked as can't be toured";
  const lastTurnOnTimeText = totalTurnOnTime
    ? `Total downtime of ${getTotalDuration(totalTurnOnTime)}.`
    : '';
  return `(${lastTourOnText}. Turned on ${totalTurnOns || 0}
    total ${pluralize('time', totalTurnOns || 0, false)}. ${lastTurnOnTimeText})`;
};

const SrHealthIndicator = ({ isHealthy, title, label }) => {
  return (
    <SrFlex styleName={classnames('SrHealthIndicator')} align="center">
      <SrBox marginBottom="auto">
        <div
          styleName={classnames('icon', {
            isQualified: isBoolean(isHealthy) ? isHealthy : false,
            isUndefined: !isBoolean(isHealthy),
          })}
        />
      </SrBox>
      <SrBox>
        <SrText font="greycliff" weight="800">
          {title}
        </SrText>
        <SrText font="greycliff" weight="500" color="grey" fontSize="16px" paddingLeft="5px">
          {label}
        </SrText>
      </SrBox>
    </SrFlex>
  );
};

const SrSunlockStatusIndicator = ({
  sunlockStatus,
  internetConnection,
  setDisplaySunlockModal,
}) => {
  const { totalDisconnectedTime, totalDisconnectionsCount, lastActiveFor } = internetConnection;

  const isActiveSunlock = sunlockStatus.status === SUNLOCK_STATUS_ACTIVE;

  const formatedPingDate = getTotalDuration(sunlockStatus.lastPingDate);
  const lastPingDateText = `Last pinged ${formatedPingDate ? `${formatedPingDate} ago` : 'never'}.`;
  const serialNumberText = `Sunlock serial number: #${sunlockStatus.serialNumber.slice(-8)}.`;
  const disconnectedText = `Disconnected ${totalDisconnectionsCount} ${pluralize(
    'time',
    totalDisconnectionsCount,
    false,
  )}.`; // eslint-disable-line max-len
  const activeTimeText =
    isActiveSunlock && lastActiveFor
      ? `Been active for the past ${getTotalDuration(lastActiveFor)}. `
      : '';
  const totalDisconnectedTimeText = `Total disconnected time of ${
    totalDisconnectedTime ? getTotalShortDuration(totalDisconnectedTime) : '0 minutes'
  }.`;
  const noInternetConnection = sunlockStatus.lastPingDate === null && lastActiveFor === null;
  const internetConnectionText = noInternetConnection
    ? 'No Internet Connection.'
    : internetConnection.isHealthy
    ? 'Strong Internet Connection:'
    : 'Poor Internet Connection:';

  const label = lodashCompact([
    lastPingDateText,
    serialNumberText,
    internetConnectionText,
    !noInternetConnection && disconnectedText,
    !noInternetConnection && activeTimeText,
    !noInternetConnection && totalDisconnectedTimeText,
  ]).join(' ');

  return (
    <SrBox paddingTop="5px">
      <SrHealthIndicator
        title={isActiveSunlock ? 'Active Sunlock Status' : `${sunlockStatus.status} Sunlock Status`}
        label={
          <>
            {`(${label}) `}
            <SrText
              onClick={() => setDisplaySunlockModal(true)}
              decoration="underline"
              font="greycliff"
              cursor="pointer"
              color="blue300"
              weight="600"
            >
              See Logs
            </SrText>
          </>
        }
        isHealthy={isActiveSunlock}
      />
    </SrBox>
  );
};

const SrToursHealth = ({
  tourHealth,
  listing,
  partner,
  market,
  showListingDetails,
  reloadListingNotes,
}) => {
  const [showTourHistory, setShowTourHistory] = useState(false);
  const [displaySunlockModal, setDisplaySunlockModal] = useState(false);

  if (!tourHealth) {
    return <div className="loading-gif" />;
  }

  const {
    cantTour,
    sunlockStatus,
    internetConnection,
    batteryLevelHealthy,
    handleLockedHealthy,
    failedToursData,
    sunlockLogsHistory,
    fixJobOrderInProgress,
  } = tourHealth;

  const onCloseNotesModal = async () => {
    if (reloadListingNotes) {
      await reloadListingNotes(listing.id);
    }
  };

  let srSunlockStatusIndicatorDOM = null;
  if (listing.sunlockId) {
    srSunlockStatusIndicatorDOM = (
      <SrSunlockStatusIndicator
        sunlockStatus={sunlockStatus}
        internetConnection={internetConnection}
        setDisplaySunlockModal={setDisplaySunlockModal}
      />
    );
  }

  let srBatteryLevelIndicatorDOM = null;
  if (listing.sunlockId) {
    srBatteryLevelIndicatorDOM = (
      <SrBox paddingTop="5px">
        <SrHealthIndicator
          title={
            sunlockStatus.batteryLevel === null
              ? 'Unknown Battery Level'
              : batteryLevelHealthy
              ? 'Good Battery Level'
              : 'Low Battery Level'
          }
          label={
            sunlockStatus.batteryLevel === null
              ? '(Not received)'
              : batteryLevelHealthy
              ? '(Above 20%)'
              : '(Needs to be changed soon)'
          }
          isHealthy={batteryLevelHealthy}
        />
      </SrBox>
    );
  }

  let fixJobOrderDOM = null;
  if (fixJobOrderInProgress) {
    const isFixJobOrderScheduled =
      fixJobOrderInProgress.signOtterStatus &&
      fixJobOrderInProgress.signOtterStatus !== SIGN_OTTER_ORDER_STATUS_SUBMITTED;

    const todayMoment = momentWithAustinTimezone();
    const dueDateMoment = momentWithAustinTimezone(fixJobOrderInProgress.dueDate);
    const dueDateString = dueDateMoment.calendar(todayMoment, {
      sameDay: '[Today]',
      nextDay: 'MMM D',
      nextWeek: 'MMM D',
      lastDay: 'MMM D',
      lastWeek: 'MMM D',
      sameElse: 'MMM D',
    });

    const fixJobOrderTitle = isFixJobOrderScheduled
      ? `Fix order scheduled for ${dueDateString}`
      : 'Fix order created, not assigned to a driver';

    fixJobOrderDOM = (
      <SrFlex
        styleName={classnames('fixOrderHeader', { scheduled: isFixJobOrderScheduled })}
        justify="between"
        align="center"
      >
        <SrFlex>
          <SrIcon className="jistix-icon" marginRight="11px" />
          <SrTextGreycliff weight="700" lineHeight="24px">
            {fixJobOrderTitle}
          </SrTextGreycliff>
        </SrFlex>
        <SrLink to={`/sunsetup/listing-orders/${listing.id}`} target="_blank">
          <SrTextGreycliff size="13" weight="800" lineHeight="20px" color="blue300">
            Link to order
          </SrTextGreycliff>
        </SrLink>
      </SrFlex>
    );
  }

  return (
    <>
      <SrCard padding="0">
        {fixJobOrderDOM}
        <SrBox padding="23px 20px 25px 20px">
          {showListingDetails && (
            <SrFlex direction="col">
              <SrLink to={`/partner/${partner.slug}/listing/${listing.id}`}>
                <SrText font="greycliff" size="16" color="blue300">
                  {partner.displayName}
                </SrText>
              </SrLink>
              <SrText
                font="greycliff"
                fontSize="21px"
                weight="800"
                letterSpacing="0"
                paddingTop="4px"
              >
                {makeListingAddressStreetDisplay(listing)}
              </SrText>
              {market && (
                <SrText font="greycliff" color="grey" size="12" weight="500">
                  {market.displayName}
                  <SrText
                    font="greycliff"
                    color="grey"
                    size="10"
                    lineHeight="18px"
                    weight="500"
                    paddingLeft="8px"
                  >
                    {listing.daysOnMarket} days on market
                  </SrText>
                </SrText>
              )}
              <SrFlex justify="space-between" marginTop="8px">
                <SrListingNotesChip
                  listingId={listing.id}
                  notesCount={listing.notesCount}
                  onModalClose={onCloseNotesModal}
                />
              </SrFlex>
              <SrSeparator marginTop="18px" />
            </SrFlex>
          )}
          <SrBox padding="16px 0px 0">
            <SrFlex justify="between">
              <SrText font="greycliff" fontSize="21px" weight="800" letterSpacing="0">
                Tour Health ({listing.sunlockId ? 'Sunlock' : 'Codebox'})
              </SrText>
            </SrFlex>
          </SrBox>
          <SrBox paddingTop="35px">
            <SrHealthIndicator
              title={cantTour.cantTour ? "Can't Tour" : 'Can Tour'}
              label={cantTour.cantTour ? getCantTourLabel(cantTour) : '(It is tour-able)'}
              isHealthy={!cantTour.cantTour}
            />
          </SrBox>
          {srSunlockStatusIndicatorDOM}
          <SrBox paddingTop="5px">
            <SrHealthIndicator
              title={handleLockedHealthy ? 'Door handle not locked' : 'Door handle may be locked'}
              label={
                handleLockedHealthy
                  ? '(Renter did not report handle locked on most recent tour.)'
                  : '(Renters reported handle locked.)'
              }
              isHealthy={handleLockedHealthy}
            />
          </SrBox>
          {srBatteryLevelIndicatorDOM}
          <div styleName={classnames('tourHistory')}>
            <SrText font="greycliff" fontSize="21px" weight="800" letterSpacing="0">
              Failed tours
            </SrText>
            <SrGridRow columns={4}>
              {[
                failedToursData.totalFailedAfterTest,
                failedToursData.totalFailedTours,
                failedToursData.totalfixOrders,
                failedToursData.totalFailedGeolocationTours,
              ].map((element, index) => (
                <SrBox
                  padding="30px 10px 0 10px"
                  key={`element-${index}`} // eslint-disable-line
                >
                  <SrText
                    font="greycliff"
                    fontSize="28px"
                    weight="800"
                    letterSpacing="0"
                    color={element === 0 ? 'grey' : index === 1 ? 'orange300' : 'red'}
                  >
                    {element}
                  </SrText>
                </SrBox>
              ))}
            </SrGridRow>
            <SrGridRow columns={4}>
              {['Failed after Fix', 'Failed total', 'Fix orders', 'Geolocation'].map((element) => (
                <SrBox padding="10px 10px 35px 10px" key={element}>
                  <SrText
                    font="greycliff"
                    fontSize="16px"
                    weight="700"
                    letterSpacing="0"
                    color="grey"
                  >
                    {element}
                  </SrText>
                </SrBox>
              ))}
            </SrGridRow>
            {tourHealth.failedToursData?.tourHistory?.length === 0 && (
              <SrText
                font="greycliff"
                size="12"
                color="grey"
                align="center"
                display="block"
                marginTop="200px"
              >
                No failed tours history for display
              </SrText>
            )}
            <SrBox marginTop="17px">
              <SrBox styleName="columns border">
                <SrText styleName="columnName" paddingLeft="16px">
                  Date
                </SrText>
                <SrText styleName="columnName" paddingLeft="108px">
                  Description
                </SrText>
              </SrBox>
              <SrToursHealthFailedToursTable
                tourHistory={tourHealth.failedToursData.tourHistory.slice(0, 1)}
              />
            </SrBox>
            {tourHealth.failedToursData?.tourHistory?.length > 1 && (
              <SrBox
                onClick={() => setShowTourHistory(!showTourHistory)}
                padding="24px 0 27px"
                cursor="pointer"
              >
                <SrText
                  font="greycliff"
                  weight="800"
                  color="blue300"
                  size="16"
                  lineHeight="19px"
                  align="center"
                  display="block"
                >
                  See all failed tours
                </SrText>
              </SrBox>
            )}
          </div>
        </SrBox>
      </SrCard>
      {displaySunlockModal && (
        <SrSunlockCommandsLogModal
          onClose={() => setDisplaySunlockModal(false)}
          address={makeListingFullAddressString(listing)}
          sunlockStatus={sunlockStatus.status}
          lastPingDate={getTotalDuration(sunlockStatus.lastPingDate)}
          sunlockSerial={sunlockStatus.serialNumber}
          sunlockLogs={sunlockLogsHistory}
        />
      )}
      {showTourHistory && (
        <SrToursHealthFailedTours
          onClose={() => setShowTourHistory(false)}
          failedToursData={failedToursData}
        />
      )}
    </>
  );
};

SrToursHealth.propTypes = {
  tourHealth: PropTypes.object.isRequired,
  listing: PropTypes.object.isRequired,
  partner: PropTypes.object,
  market: PropTypes.object,
  showListingDetails: PropTypes.bool,
};

SrToursHealth.displayName = 'SrToursHealth';

export default SrToursHealth;
